import {Channel as TypeChannel, ChannelFilters, ChannelSort, DefaultGenerics, StreamChat} from "stream-chat";
import {
  Avatar,
  Channel,
  ChannelHeader,
  ChannelList,
  ChannelPreviewUIComponentProps,
  ChannelSearchFunctionParams,
  ChannelSearchProps,
  Chat,
  InfiniteScroll,
  LoadingIndicator,
  MessageList,
  Window
} from "stream-chat-react";
import "stream-chat-react/dist/css/v2/index.css";
import "./DispatchMessaging.css";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useAuthState} from "../../providers/AuthProvider";
import {useAppContext} from "../../ApplicationContext";
import {ChatTitle, CustomChannelPreview, CustomSearchResultItem} from "./DispatchMessagingCommon";
import styled from "@emotion/styled";
import log from "loglevel";
import {Button, Drawer} from "@blueprintjs/core";
import {InforIcon} from "../job/components/AssignmentBarV2";
import {DefaultStreamChatGenerics} from "stream-chat-react/dist/types/types";
import useChatUserDetails from "./useChatUserDetails";
import {useFeatureFlagContext} from "../../providers/FeatureFlagProvider";
import {useDispatchGroupContext} from "../../providers/DispatchGroupProvider";
import {CustomMessage} from "./CustomMessage";
import {extractJsonPref, extractSimplePref, PreferenceContext} from "../../providers/PreferenceProvider";
import {TenantPreferences, UserPreferences} from "../../constants/constants";
import {getDefaultCardConfigurations} from "../manifest/card-configuration/v2/constants/constants";
import {IManifestCardConfigurationsV2} from "../manifest/card-configuration/v2/types/manifest-card-configurations.type";
import DispatchMessageInput from "./DispatchMessageInput";
import {isEqual} from "lodash";
import {DriverInformationValue} from "../manifest/card-configuration/v2/constants/enums";
import {
  applyUnreadEvent,
  getDriverChannelCid,
  getSelectedDriverUnreadFromEvent,
  resolveSelectedDriverChannel,
  toUnreadChannels,
  UnreadChannels
} from "./DispatchMessaging.service";
type DispatchMessagingProps = {
  driverId?: number;
};

export type TSetSeletedChannel = (channel: TypeChannel<DefaultStreamChatGenerics> | undefined) => Promise<void>;

const sort: ChannelSort<DefaultGenerics> = {has_unread: -1, last_message_at: -1};

export const getDisplayTitleV2 = (
  channel: TypeChannel<DefaultStreamChatGenerics> | undefined,
  cardConfigurationState: IManifestCardConfigurationsV2
): string => {
  const retVal = cardConfigurationState.driverInformation?.includes(DriverInformationValue.DriverName)
    ? channel?.data?.name
    : (channel?.data?.driver_code as string);
  return retVal ?? "";
};

const DispatchMessaging = ({driverId}: DispatchMessagingProps) => {
  const {driverMessaging} = useFeatureFlagContext();
  const {isSuperUser, tenantId} = useAuthState();
  const {
    appState: {isChatOpen, selectedDriverChannelId},
    dispatch
  } = useAppContext();
  const {isDispatchGroupLoaded, driverGroups} = useDispatchGroupContext();
  const [selectedChannel, setSelectedChannel] = useState<TypeChannel<DefaultStreamChatGenerics> | undefined>();
  const {userPreferences, tenantPreferences} = useContext(PreferenceContext);
  const isUseETAService = extractSimplePref(tenantPreferences, TenantPreferences.useEtaService, true).value;

  const [chatClient, setChatClient] = useState<StreamChat | null>();
  const chatUserDetails = useChatUserDetails(driverMessaging);
  const [unreadState, setUnreadState] = useState<Map<string, {unreadCount: number; dispatchGroupId: number}>>(
    new Map()
  );
  // Channels with unread messages for this user, across every dispatch group
  const unreadChannelsRef = useRef<UnreadChannels | undefined>();
  const [isResolvingDriverChannel, setIsResolvingDriverChannel] = useState(false);

  const cardConfigurationsV2: IManifestCardConfigurationsV2 = useMemo(
    () =>
      extractJsonPref(
        userPreferences,
        UserPreferences.manifestCardConfigurations,
        getDefaultCardConfigurations(isUseETAService)
      ).value,
    [userPreferences, isUseETAService]
  );

  const {userId, apiKey, userToken} = chatUserDetails ?? {};

  const channelTitle = useMemo(
    () => getDisplayTitleV2(selectedChannel, cardConfigurationsV2),
    [cardConfigurationsV2, selectedChannel]
  );

  const dispatchGroupIds = useMemo(
    () => driverGroups.map((driverGroup) => driverGroup.dispatchGroupId),
    [driverGroups]
  );

  const filters = useMemo(() => {
    const retValue: ChannelFilters = {
      team: {$eq: tenantId},
      type: {$eq: "driver"}
    } as any;
    if (!isSuperUser) {
      retValue.members = {$in: [userId ?? ""]};
    }
    if (dispatchGroupIds.length > 0) {
      retValue.dispatch_group_id = {$in: dispatchGroupIds};
    }
    return retValue;
  }, [dispatchGroupIds, isSuperUser, tenantId, userId]);

  const cacheFilters = useRef<ChannelFilters>(filters);

  const searchTimer = useRef<NodeJS.Timeout | null>(null);

  const isEventBelongsToCurrentDispatchGroup = useCallback(
    (eventDispatchGroupId: number | undefined) => {
      if (
        dispatchGroupIds &&
        dispatchGroupIds.length > 0 &&
        eventDispatchGroupId &&
        !dispatchGroupIds.includes(eventDispatchGroupId)
      ) {
        return false;
      }
      return true;
    },
    [dispatchGroupIds]
  );

  const customSearchFunction = useCallback(
    async (props: ChannelSearchFunctionParams, event: {target: {value: string}}, client: StreamChat) => {
      // eslint-disable-next-line react/prop-types
      const {setResults, setSearching, setQuery} = props;
      const value = event.target.value;
      setQuery(value);
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
      }
      searchTimer.current = setTimeout(() => {
        if (value.trim()) {
          executeSearch(value);
        } else {
          setResults([]);
        }
      }, 300);

      const executeSearch = async (value: string) => {
        const customFilters: ChannelFilters = {
          ...filters,
          $and: [
            {
              $or: [{name: {$autocomplete: value}}, {driver_code: {$autocomplete: value}}]
            }
          ]
        };
        setSearching(true);
        const channels = await client.queryChannels(customFilters, sort);
        setResults(channels);
        setSearching(false);
      };
    },
    [filters]
  );

  const onSelecteChannel: TSetSeletedChannel = useCallback(
    async (channel: TypeChannel<DefaultStreamChatGenerics> | undefined) => {
      if (channel) {
        setSelectedChannel(channel);
      }
    },
    []
  );

  const additionalChannelSearchProps: Omit<ChannelSearchProps<DefaultGenerics>, "setChannels"> = useMemo(() => {
    return {
      searchFunction(params, event) {
        if (chatClient) {
          return customSearchFunction(params, event, chatClient);
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      SearchResultItem: (props) => CustomSearchResultItem({...props, setSelectedChannel: onSelecteChannel})
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, chatClient]);

  const renderAvatar = useCallback(
    () => <Avatar name={selectedChannel?.data?.name} size={39} />,
    [selectedChannel?.data?.name]
  );

  const renderMessage = useCallback(() => {
    return <CustomMessage driverTitle={channelTitle} />;
  }, [channelTitle]);

  const renderPreviewChannel = useCallback(
    (props: ChannelPreviewUIComponentProps<DefaultGenerics>) => (
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      <CustomChannelPreview
        {...props}
        setSelectedChannel={onSelecteChannel}
        active={selectedChannel?.cid === props?.channel?.cid}
        unread={unreadState.get(props?.channel?.cid ?? "")?.unreadCount ?? 0}
      />
    ),
    [onSelecteChannel, selectedChannel?.cid, unreadState]
  );

  const connect = useCallback(async (userId, apiKey, userToken) => {
    try {
      log.debug("Messaging: Connecting to streamchat");
      const client = StreamChat.getInstance(apiKey);
      const res = await client.connectUser({id: userId}, userToken);
      log.debug("Messaging: Connected to streamchat!");
      return {res, client};
    } catch (e) {
      log.error("Error connecting to streamchat", e);
      return null;
    }
  }, []);

  useEffect(() => {
    const totalUnreadCount = Array.from(unreadState.values()).filter(
      (item) => item.unreadCount > 0 && isEventBelongsToCurrentDispatchGroup(item.dispatchGroupId)
    ).length;
    dispatch({
      type: "SetTotalUnreadChannel",
      payload: totalUnreadCount
    });
  }, [dispatch, isEventBelongsToCurrentDispatchGroup, unreadState]);

  useEffect(() => {
    if (userId && apiKey && userToken && isDispatchGroupLoaded) {
      connect(userId, apiKey, userToken).then((response) => {
        if (!response) return;
        const {client} = response;
        setChatClient(client);
      });
    }
  }, [apiKey, connect, dispatch, userId, userToken, isDispatchGroupLoaded]);

  useEffect(() => {
    if (!chatClient) {
      return;
    }
    let isUnmounted = false;
    // One GET /unread per connect and per recovered connection; it is not billed as a Query Channels call
    const loadUnreadChannels = () => {
      unreadChannelsRef.current = undefined;
      chatClient
        .getUnreadCount()
        .then((response) => {
          if (!isUnmounted) {
            unreadChannelsRef.current = toUnreadChannels(response.channels);
          }
        })
        .catch((error) => log.error("Messaging: Error loading unread channels", error));
    };
    loadUnreadChannels();
    const listener = chatClient.on((event) => {
      if (event.type === "connection.recovered") {
        loadUnreadChannels();
      } else {
        unreadChannelsRef.current = applyUnreadEvent(unreadChannelsRef.current, event, chatClient.userID);
      }
    });
    return () => {
      isUnmounted = true;
      listener.unsubscribe();
    };
  }, [chatClient]);

  useEffect(() => {
    let clientEventListener:
      | {
          unsubscribe: () => void;
        }
      | undefined;

    if (chatClient && !isSuperUser) {
      clientEventListener = chatClient.on((event) => {
        if (event.type === "connection.changed" && event.online === true) {
          log.debug("Messaging: Re-watching channels");
          chatClient.queryChannels(filters, sort, {
            watch: true,
            state: true
          });
        }

        if (selectedDriverChannelId && event.cid === selectedDriverChannelId) {
          const totalUnreadCount = getSelectedDriverUnreadFromEvent(event, selectedDriverChannelId);
          if (totalUnreadCount !== undefined) {
            dispatch({
              type: "SetSelectedDriverUnreadCount",
              payload: totalUnreadCount
            });
          }
        } else if (event.type === "channels.queried") {
          setUnreadState((prevState) => {
            const newState = new Map(prevState);
            (event?.queriedChannels?.channels ?? []).forEach((channel) => {
              const eventChannelDispatchGroupId = (channel.channel as any)?.dispatch_group_id;
              const unreadCount = channel.read?.find((read) => read.user.id === userId)?.unread_messages ?? 0;
              newState.set(channel.channel.cid, {
                unreadCount,
                dispatchGroupId: eventChannelDispatchGroupId
              });
            });
            return newState;
          });
        } else if (event.type === "message.new") {
          log.debug("message.new event received", event);
          if (event?.user?.role !== "driver") {
            return;
          }
          setUnreadState((prevState) => {
            const eventChannelDispatchGroupId = (event?.channel_custom as any)?.dispatch_group_id;
            const newState = new Map(prevState);
            const unread = (newState.get(event.cid as string)?.unreadCount ?? 0) + 1;
            newState.set(event.cid as string, {
              unreadCount: unread,
              dispatchGroupId: eventChannelDispatchGroupId
            });
            return newState;
          });
        } else if (event.type === "message.read") {
          log.debug("message.read event received", event);
          if (event?.user?.id !== userId) {
            return;
          }
          setUnreadState((prevState) => {
            const newState = new Map(prevState);
            newState.delete(event.cid as string);
            return newState;
          });
        }
      });
    }

    return () => {
      clientEventListener?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatClient, dispatch, driverGroups, selectedDriverChannelId, userId, isSuperUser]);

  useEffect(() => {
    setSelectedChannel(undefined);
    if (!isEqual(cacheFilters.current, filters)) {
      setUnreadState(new Map());
      cacheFilters.current = filters;
    }
  }, [filters]);

  // A newer lookup, or a change of the selected driver, drops the result of the one in flight
  const cancelDriverLookupRef = useRef<() => void>();
  const lookupDriverChannel = useCallback(
    (lookupDriverId: number) => {
      if (!chatClient) {
        return;
      }
      cancelDriverLookupRef.current?.();
      let isCancelled = false;
      setIsResolvingDriverChannel(true);
      chatClient
        .queryChannels(
          {
            ...filters,
            driver_id: {
              $eq: lookupDriverId
            }
          },
          sort
        )
        .then((queryDriverChannelRes) => {
          if (isCancelled || queryDriverChannelRes.length === 0) {
            return;
          }
          const driverChannel = queryDriverChannelRes[0];
          dispatch({type: "SetSelectedDriverChannelId", payload: driverChannel.cid});
          dispatch({type: "SetSelectedDriverUnreadCount", payload: driverChannel.state.unreadCount > 0 ? 1 : 0});
          setSelectedChannel(driverChannel);
        })
        .finally(() => {
          if (!isCancelled) {
            setIsResolvingDriverChannel(false);
          }
        });
      cancelDriverLookupRef.current = () => {
        isCancelled = true;
        setIsResolvingDriverChannel(false);
      };
    },
    [chatClient, dispatch, filters]
  );

  useEffect(() => {
    if (!chatClient || !tenantId) {
      return;
    }
    if (driverId === undefined) {
      dispatch({type: "SetSelectedDriverChannelId", payload: undefined});
      dispatch({type: "SetSelectedDriverUnreadCount", payload: 0});
      setSelectedChannel(undefined);
      return;
    }
    // With the chat closed, answer from channels this tab already holds; queryChannels is billed per call
    const cid = getDriverChannelCid(tenantId, driverId);
    const {channel, hasUnread, needsLookup} = resolveSelectedDriverChannel({
      activeChannels: chatClient.activeChannels,
      cid,
      dispatchGroupIds,
      unreadChannels: unreadChannelsRef.current
    });
    dispatch({type: "SetSelectedDriverChannelId", payload: cid});
    dispatch({type: "SetSelectedDriverUnreadCount", payload: hasUnread ? 1 : 0});
    setSelectedChannel(channel);
    if (isChatOpen || needsLookup) {
      lookupDriverChannel(driverId);
    }
    return () => cancelDriverLookupRef.current?.();
    // isChatOpen is read, not a dependency: closing and reopening the chat keeps the conversation the user picked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatClient, dispatch, dispatchGroupIds, driverId, filters, lookupDriverChannel, tenantId]);

  useEffect(() => {
    // The chat opens for a selected driver whose channel was not resolved while it was closed
    if (isChatOpen && driverId !== undefined && !selectedChannel) {
      lookupDriverChannel(driverId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChatOpen]);

  return (
    <StyledChat
      isShow={isChatOpen}
      isOpen={true}
      enforceFocus={false}
      isCloseButtonShown={true}
      canOutsideClickClose={false}
      hasBackdrop={false}
      position="bottom-right"
      portalClassName="chat-portal"
    >
      {chatClient ? (
        <Chat client={chatClient}>
          <Container>
            <LeftContainer data-testid="chat-modal">
              <ChatTitle />
              <ChannelList
                filters={filters}
                sort={sort}
                showChannelSearch
                setActiveChannelOnMount={false}
                Paginator={InfiniteScroll}
                Preview={renderPreviewChannel}
                additionalChannelSearchProps={additionalChannelSearchProps}
                channelRenderFilterFn={(channels) => {
                  return channels.filter((channel) => {
                    const dispatch_group_ids = (filters?.dispatch_group_id as any)?.$in;
                    if (dispatch_group_ids && dispatch_group_ids.length > 0) {
                      return dispatch_group_ids.includes(channel.data?.dispatch_group_id);
                    }
                    return true;
                  });
                }}
              />
            </LeftContainer>
            <RightContainer>
              <CloseButton
                minimal
                icon="cross"
                data-testid="close-chat-button"
                onClick={() => {
                  dispatch({
                    type: "SetIsChatOpen",
                    payload: false
                  });
                }}
              />
              {selectedChannel && isChatOpen ? (
                <Channel channel={selectedChannel}>
                  <Window>
                    <ChannelHeader title={channelTitle} Avatar={renderAvatar} />
                    <MessageList
                      Message={renderMessage}
                      messageActions={[]}
                      returnAllReadData
                      scrolledUpThreshold={50}
                    />
                    <DispatchMessageInput />
                  </Window>
                </Channel>
              ) : isResolvingDriverChannel ? (
                <LoadingIndicator />
              ) : (
                <NoChannelContainer>
                  <InforText>
                    <InforIcon />
                    <span>Select the Driver to See the Chat or Send a Message</span>
                  </InforText>
                </NoChannelContainer>
              )}
            </RightContainer>
          </Container>
        </Chat>
      ) : (
        <LoadingIndicator />
      )}
    </StyledChat>
  );
};

export default DispatchMessaging;

const StyledChat = styled(Drawer)<{isShow: boolean}>`
  font-family: "Roboto", sans-serif;
  height: ${(props) => (props.isShow ? "50vh" : "0 !important")};
  min-height: ${(props) => (props.isShow ? "530px" : "0 !important")};
  bottom: 12px !important;
  right: 12px !important;
  left: calc(100vw - 600px) !important;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0px 4px 4px 0px #00000040;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
`;

const LeftContainer = styled.div`
  width: 224px;
  box-shadow: 0px 4px 4px 0px #00000040;
  display: flex;
  flex-direction: column;
  flex: 1 1;
`;

const RightContainer = styled.div`
  position: relative;
  flex-grow: 1;
  visibility: visible;
`;

const CloseButton = styled(Button)`
  position: absolute;
  right: 12px;
  top: 12px;
  z-index: 1000;
`;

const NoChannelContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InforText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 5px;
  justify-content: center;
  max-width: 70%;
`;
